import IProcedureDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureDetail';
import React, { ChangeEvent, useState, useEffect, useRef } from 'react';
import ProcedureActionEditor from './ProcedureActionEditor';
import {
  EditorChangeEvent,
  EditorTools,
  EditorToolsSettings,
  applyInlineStyle
} from '@Shared.Angular/kendo.react/KendoReactEditor';
import KendoReactEditor from '@Shared.Angular/kendo.react/KendoReactEditor';
import { getService } from '@Client/runner.hooks/useService';
import IProcedureActionDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureActionDetail';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import ClipboardCopy from '@Shared.Angular/flowingly.components/ClipboardCopy/ClipboardCopy';
import PublishProcedureModal from './PublishProcedureModal';
import ProcedureDetailsModal from './ProcedureDetailsModal';
import ProcedureActionImageView from '@Shared.Angular/flowingly.components/Procedures/ProcedureActionImageView';
import IconButton from '@Shared.Angular/flowingly.components/buttons/IconButton';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';
import { ProcedureViewMode } from '@Shared.Angular/@types/core/contracts/constants/procedureViewMode';
import { ProcedureEnhancementState } from '@Shared.Angular/@types/core/contracts/constants/procedureEnhancementState';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import { Spinner } from '@Shared.Angular/flowingly.components/Spinner/Spinner';
import { ProcedureVersionHistoryPanel } from './ProcedureVersionHistoryPanel';
import { ResizablePanel } from '@Shared.Angular/flowingly.components/ResizablePanel/ResizablePanel';
import { useDragReorder } from '@Shared.Angular/flowingly.hooks/useDragReorder';
import { copyProcedureToClipboard } from './procedure.clipboard';

const ForeColorGradient = (props: EditorTools.ForeColorProps) => (
  <EditorTools.ForeColor
    {...props}
    colorPickerProps={{
      icon: EditorToolsSettings.foreColor.colorPickerProps.icon,
      svgIcon: EditorToolsSettings.foreColor.colorPickerProps.svgIcon,
      view: 'gradient',
      gradientSettings: { opacity: false },
      onChange: (e) => {
        if (props.view && e.value) {
          applyInlineStyle({ style: 'color', value: e.value }, 'ForeColor')(
            props.view.state,
            props.view.dispatch
          );
        }
        // this will prevent the editor from closing BUT this will error. we want this because it interrupts the closing of the color picker and the user experience. we need to find a better way to do this.
        throw new Error('Preventing editor from closing on color change');
      }
    }}
  />
);
const BackColorGradient = (props: EditorTools.BackColorProps) => (
  <EditorTools.BackColor
    {...props}
    colorPickerProps={{
      icon: EditorToolsSettings.backColor.colorPickerProps.icon,
      svgIcon: EditorToolsSettings.backColor.colorPickerProps.svgIcon,
      view: 'gradient',
      gradientSettings: { opacity: false },
      onChange: (e) => {
        if (props.view && e.value) {
          applyInlineStyle(
            { style: 'background-color', value: e.value },
            'BackColor'
          )(props.view.state, props.view.dispatch);
        }
        // this will prevent the editor from closing BUT this will error. we want this because it interrupts the closing of the color picker and the user experience. we need to find a better way to do this.
        throw new Error('Preventing editor from closing on color change');
      }
    }}
  />
);

type Props = {
  procedure: IProcedureDetail;
  editorMode: ProcedureViewMode;
};

const ProcedureEditor = (props: Props) => {
  const isReadOnlyView =
    props.editorMode === ProcedureViewMode.ReadOnly ||
    props.editorMode === ProcedureViewMode.Public;
  const isPublicView = props.editorMode === ProcedureViewMode.Public;
  const isPublishRequest =
    props.editorMode === ProcedureViewMode.PublishRequest;
  const isLoggedIn = getService('sessionService').isLoggedIn();
  const isEnhancing =
    props.procedure.enhancementState === ProcedureEnhancementState.InProgress;

  const [procedure, setProcedure] = useState(() => {
    props.procedure.actions?.sort((a, b) => a.order - b.order);
    return props.procedure;
  });
  const [procedureName, setProcedureName] = useState(procedure.name);
  const [procedureDescription, setProcedureDescription] = useState(
    procedure.description
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [isMobile] = useState<boolean>(
    getService('browserUtilsService').isMobileDevice()
  );
  const [imageViewIndex, setImageViewIndex] = useState<number | null>(null);
  const [isActionRequestInProgress, setIsActionRequestInProgress] =
    useState<boolean>(false);
  const [isDocumentRequestInProgress, setIsDocumentRequestInProgress] =
    useState<boolean>(false);
  const [activeActionId, setActiveActionId] = useState<Guid | null>(
    procedure.actions[0]?.id || null
  );
  const [isSavingProcedure, setIsSavingProcedure] = useState(false);
  const [showDuplicateNameError, setShowDuplicateNameError] = useState(false);
  const [versionHistoryRefreshKey, setVersionHistoryRefreshKey] = useState(0);
  const [isCopyingSop, setIsCopyingSop] = useState(false);

  const updateName = (e: ChangeEvent<HTMLInputElement>) => {
    setProcedureName(e.target.value);
    setShowDuplicateNameError(false);
  };
  const updateDescription = (e: EditorChangeEvent) => {
    setProcedureDescription(e.html);
  };
  const procedureApiService = getService('procedureApiService');
  const fileService = getService('fileService');
  const validationService = getService('validationService');
  const notificationService = getService('notificationService');
  const $state = getService('$state');

  const copySopToClipboard = async () => {
    if (isCopyingSop) {
      return;
    }

    setIsCopyingSop(true);

    try {
      await copyProcedureToClipboard(
        procedure,
        fileService,
        validationService.sanitizeString
      );

      notificationService.showSuccessToast(
        'SOP copied to clipboard. Text, images, and the recording link are ready to paste.'
      );
    } catch {
      notificationService.showErrorToast('Failed to copy SOP to clipboard');
    } finally {
      setIsCopyingSop(false);
    }
  };

  const saveChanges = async () => {
    if (isSavingProcedure) return;

    const trimmedName = procedureName?.trim() ?? '';
    if (!trimmedName) {
      notificationService.showErrorToast('SOP name cannot be empty');
      return;
    }

    // Only check for duplicate names if the name has changed
    if (trimmedName !== procedure.name?.trim()) {
      try {
        const isNameUnique = await procedureApiService.checkIsNameUnique(
          trimmedName,
          procedure.id
        );
        if (!isNameUnique) {
          setShowDuplicateNameError(true);
          return;
        }
      } catch (error) {
        notificationService.showErrorToast('Failed to validate SOP name');
        return;
      }
    }

    setIsSavingProcedure(true);
    const updatedProcedure = {
      ...procedure,
      name: trimmedName,
      description: procedureDescription
    };

    try {
      await procedureApiService.updateProcedure(updatedProcedure);
      setProcedure(updatedProcedure);
      setIsEditMode(false);
    } catch (error) {
      notificationService.showErrorToast('Failed to save SOP');
    } finally {
      setIsSavingProcedure(false);
    }
  };

  const cancelChanges = () => {
    setProcedureName(procedure.name);
    setProcedureDescription(procedure.description);
    setShowDuplicateNameError(false);
    setIsEditMode(false);
  };
  const actionUpdated = (updatedAction: IProcedureActionDetail) => {
    setProcedure((prev) => {
      const actions = updatedAction.active
        ? prev.actions.map((action) =>
            action.id === updatedAction.id ? updatedAction : action
          )
        : prev.actions.filter((action) => action.id !== updatedAction.id);
      return {
        ...prev,
        actions: actions
      };
    });
  };

  const tocItemClicked = (actionId: Guid) => {
    document
      .getElementById('action_' + actionId)
      .scrollIntoView({ behavior: 'smooth' });
    // Actions at the end won't get selected by the scroll event handler
    // because they can't reach the top of the container
    setTimeout(() => setActiveActionId(actionId), 1000);
  };

  const tocKeyPress = (event: React.KeyboardEvent, actionId: Guid) => {
    if (event.key === 'Enter') {
      tocItemClicked(actionId);
    }
  };

  const isEditModeEnabled = props.editorMode === ProcedureViewMode.Edit;

  const handleReorderActions = (reorderedActions: IProcedureActionDetail[]) => {
    const actionOrders = reorderedActions.map((action, index) => ({
      id: action.id,
      order: index
    }));

    const updatedActions = reorderedActions.map((action, index) => ({
      ...action,
      order: index
    }));

    setProcedure((prev) => ({
      ...prev,
      actions: updatedActions
    }));

    procedureApiService
      .reorderProcedureActions(procedure.id, actionOrders)
      .catch(() => {
        notificationService.showErrorToast('Failed to reorder steps');
        setProcedure((prev) => ({
          ...prev,
          actions: procedure.actions
        }));
      });
  };

  const { dragState, handlers } = useDragReorder(
    procedure.actions,
    handleReorderActions
  );

  const handlePublishedProcedure = (publishedProcedure: IProcedureDetail) => {
    publishedProcedure.actions?.sort((a, b) => a.order - b.order);
    setProcedure(publishedProcedure);
    setIsPublishModalOpen(false);
    setVersionHistoryRefreshKey((prev) => prev + 1);
  };

  const addAction = (afterIndex: number) => {
    if (isActionRequestInProgress) return;
    setIsActionRequestInProgress(true);
    const order =
      afterIndex === -1 ? 0 : procedure.actions[afterIndex].order + 1;
    const newAction = {
      id: EmptyGuid,
      procedureId: procedure.id,
      procedureSchemaId: procedure.actions[0]?.procedureSchemaId || EmptyGuid,
      title: '',
      description: '',
      order: order,
      active: true,
      overlays: []
    } as IProcedureActionDetail;

    procedureApiService
      .addProcedureAction(newAction)
      .then((newActionId: Guid) => {
        newAction.id = newActionId;
        const afterActions = procedure.actions.slice(afterIndex + 1);
        afterActions.forEach((action, index) => {
          action.order = order + index + 1;
        });
        setProcedure((prev) => ({
          ...prev,
          actions: [
            ...prev.actions.slice(0, afterIndex + 1),
            newAction,
            ...afterActions
          ]
        }));
        setIsActionRequestInProgress(false);
      })
      .catch(() => {
        setIsActionRequestInProgress(false);
      });
  };

  const goToEditMode = () => {
    // We redirect instead of turning off readOnly
    // because there may be draft changes not currently loaded
    $state.go(
      STATE_MAPPINGS.procedure,
      { procedureId: procedure.id, viewMode: ProcedureViewMode.Edit },
      {}
    );
  };

  const downloadPdf = () => {
    const documentApiService = getService('documentApiService');
    setIsDocumentRequestInProgress(true);
    documentApiService.downloadProcedurePdf(procedure.id).finally(() => {
      setIsDocumentRequestInProgress(false);
    });
  };

  // Setup scroll event to track which action is at the top of the visible area
  const contentRowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!procedure.actions.length) return;

      const scrollContainer = contentRowRef.current;
      if (!scrollContainer) return;

      const actionElements = procedure.actions
        .map((action) => document.getElementById(`action_${action.id}`))
        .filter(Boolean);

      if (!actionElements.length) return;

      const containerRect = scrollContainer.getBoundingClientRect();

      for (let i = 0; i < actionElements.length; i++) {
        const actionElement = actionElements[i];
        const actionRect = actionElement.getBoundingClientRect();

        const actionYCutoff = actionRect.height / 3 + actionRect.top;
        if (actionYCutoff > containerRect.top) {
          setActiveActionId(procedure.actions[i].id);
          break;
        }
      }
    };

    const scrollContainer = contentRowRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      handleScroll();
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [procedure.actions]);

  const editorTools = [
    EditorTools.Bold,
    EditorTools.Italic,
    EditorTools.Underline,
    EditorTools.Strikethrough,
    ForeColorGradient,
    BackColorGradient,
    EditorTools.CleanFormatting,
    EditorTools.AlignLeft,
    EditorTools.AlignCenter,
    EditorTools.AlignRight,
    EditorTools.AlignJustify,
    EditorTools.Indent,
    EditorTools.Outdent,
    EditorTools.UnorderedList,
    EditorTools.OrderedList,
    EditorTools.FontName,
    EditorTools.FontSize,
    EditorTools.FormatBlock,
    EditorTools.Link,
    EditorTools.Unlink
  ];
  const isGovernanceEnabled = getService('APP_CONFIG').enableGovernance;
  const userCanApprove =
    isGovernanceEnabled &&
    procedure.userCanPublish &&
    props.editorMode === ProcedureViewMode.PublishRequest;
  const userCanPublish =
    procedure.userCanPublish && props.editorMode === ProcedureViewMode.Edit;
  const userCanPublishRequest =
    isGovernanceEnabled &&
    procedure.userCanEdit &&
    props.editorMode === ProcedureViewMode.Edit;
  const showPublishButton =
    userCanApprove || userCanPublish || userCanPublishRequest;
  const currentProcedureVersion = isReadOnlyView
    ? `V${procedure.version}`
    : null;

  return (
    procedure && (
      <div
        id="editor-container"
        className={
          (isMobile ? 'mobile ' : '') +
          (isReadOnlyView ? 'read-only ' : '') +
          (isPublicView ? 'public-view' : '')
        }
      >
        <div className="header-row">
          {isPublicView && (
            <div
              className="public-title"
              title="Learn more at flowingly.io"
              onClick={() => window.open('https://flowingly.io', '_blank')}
            >
              <img src={ASSETS_PATH + '/icon_green.png'} alt="Flowingly" />
              <div>Flowingly SOP Recorder</div>
            </div>
          )}
          <div className="header-buttons">
            {!isPublicView && (
              <TextButton
                text="Copy"
                id="procedure-copy-button"
                type="tertiary"
                onClick={copySopToClipboard}
                disabled={isCopyingSop}
                testId="procedure-copy-clipboard-button"
                title="Copy to clipboard"
              />
            )}
            {isReadOnlyView && !isPublicView && (
              <TextButton
                text="Print"
                id="procedure-print-button"
                type="tertiary"
                onClick={downloadPdf}
                disabled={isDocumentRequestInProgress}
              />
            )}
            {(procedure.userCanEdit || procedure.userCanPublish) &&
              !isReadOnlyView &&
              !isPublicView && (
                <>
                  <TextButton
                    text={'Version History'}
                    type="secondary"
                    disabled={isEnhancing}
                    id="procedure-version-history-button"
                    onClick={() =>
                      setIsVersionHistoryOpen(!isVersionHistoryOpen)
                    }
                  />
                  <TextButton
                    text="Details"
                    type="secondary"
                    disabled={isEnhancing}
                    id="procedure-details-button"
                    onClick={() => setIsDetailsModalOpen(true)}
                  />
                </>
              )}
            {showPublishButton && (
              <TextButton
                text="Publish"
                type="primary"
                disabled={isEnhancing}
                id="procedure-publish-button"
                onClick={() => setIsPublishModalOpen(true)}
              />
            )}
            {procedure.userCanEdit && isReadOnlyView && !isPublicView && (
              <TextButton
                text="Edit"
                id="procedure-edit-button"
                type="secondary"
                onClick={goToEditMode}
              />
            )}
            {isPublicView && (
              <>
                <div className="public-actions-copy-link">
                  <ClipboardCopy
                    label="Copy link "
                    copyText={window.location.href}
                  ></ClipboardCopy>
                </div>
                <a href={isLoggedIn ? '/' : '/login'} className="blue-text">
                  Sign in to Flowingly
                </a>
              </>
            )}
          </div>
        </div>

        <div className="content-row" ref={contentRowRef}>
          {!isMobile && (
            <div className="column-left">
              <div className="table-of-contents">
                <div className="toc-heading">Table of Contents</div>
                <div
                  className={`toc-items ${
                    isEditModeEnabled ? 'drag-reorder-container' : ''
                  }`}
                >
                  {procedure.actions.map((action, index) => (
                    <React.Fragment key={action.id}>
                      {/* Drop zone before item */}
                      {isEditModeEnabled &&
                        dragState.isDragging &&
                        dragState.dragIndex !== index &&
                        dragState.dragIndex !== index - 1 && (
                          <div
                            className={`drop-zone visible ${
                              dragState.dropIndex === index ? 'active' : ''
                            }`}
                            onDragOver={handlers.handleDragOver(index)}
                            onDragLeave={handlers.handleDragLeave}
                            onDrop={handlers.handleDrop(index)}
                          >
                            {dragState.dropIndex === index ? 'Drop here' : ''}
                          </div>
                        )}
                      {/* Drop zone before item */}
                      <div
                        className={`toc-item ${
                          activeActionId === action.id ? 'active' : ''
                        } ${
                          dragState.isDragging && dragState.dragIndex === index
                            ? 'drag-origin'
                            : ''
                        }`}
                        key={action.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => tocItemClicked(action.id)}
                        onKeyDown={(e) => tocKeyPress(e, action.id)}
                        data-testid={`toc-item-${index + 1}`}
                      >
                        {isEditModeEnabled && (
                          <div
                            className="drag-handle"
                            draggable={isEditModeEnabled}
                            onDragStart={handlers.handleDragStart(index)}
                            onDragEnd={handlers.handleDragEnd}
                          >
                            <i className="fa-solid fa-grip-vertical" />
                          </div>
                        )}
                        <div className="toc-index">
                          {index < 9 ? '0' : ''}
                          {index + 1}
                        </div>
                        <div className="toc-title">
                          {dragState.isDragging && dragState.dragIndex === index
                            ? `You're moving '${action.title}' from here`
                            : action.title}
                        </div>
                      </div>
                      {/* Drop zone after last item (hide if dragging the last item) */}
                      {isEditModeEnabled &&
                        index === procedure.actions.length - 1 &&
                        dragState.isDragging &&
                        dragState.dragIndex !== index && (
                          <div
                            className={`drop-zone visible ${
                              dragState.dropIndex === index + 1 ? 'active' : ''
                            }`}
                            onDragOver={handlers.handleDragOver(index + 1)}
                            onDragLeave={handlers.handleDragLeave}
                            onDrop={handlers.handleDrop(index + 1)}
                          >
                            {dragState.dropIndex === index + 1
                              ? 'Drop here'
                              : ''}
                          </div>
                        )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div className="column-center">
            {!isReadOnlyView && isEnhancing && (
              <div id="ai-enhancement-banner">
                <Spinner /> AI enhancement in progress, you can leave this page
                while it runs.
              </div>
            )}
            {isEditMode && (
              <div className="editor">
                {showDuplicateNameError && (
                  <div className="validation-errors">
                    <p className="center-align">
                      There is already a SOP with this name. Please enter a
                      unique name.
                    </p>
                  </div>
                )}
                <div className="procedure-field">
                  <label htmlFor="procedure-name">Name</label>
                  <input
                    type="text"
                    id="procedure-name"
                    value={procedureName}
                    onChange={updateName}
                    required
                  ></input>
                </div>
                <div className="procedure-field">
                  <label>Description</label>
                  <KendoReactEditor
                    tools={editorTools}
                    defaultContent={procedureDescription || ''}
                    onChange={updateDescription}
                    resizable={true}
                    contentStyle={{ height: '100%' }}
                    style={{ resize: 'vertical' }}
                  />
                </div>
                <div className="editor-buttons">
                  <TextButton
                    text={isSavingProcedure ? 'Saving...' : 'Save'}
                    type="secondary"
                    onClick={saveChanges}
                    disabled={isSavingProcedure}
                  />
                  <TextButton
                    text="Cancel"
                    type="text-only"
                    onClick={cancelChanges}
                    disabled={isSavingProcedure}
                  />
                </div>
              </div>
            )}
            {!isEditMode && (
              <div className="editor readonly">
                <div className="header">
                  <div className="procedure-field">
                    <h3>{procedure.name}</h3>
                  </div>
                  {isReadOnlyView && currentProcedureVersion && (
                    <div className="procedure-version-label">
                      {currentProcedureVersion}
                    </div>
                  )}
                  {!isReadOnlyView && !isPublishRequest && !isEnhancing && (
                    <div className="editor-toolbar">
                      <IconButton
                        iconClass="fa-pencil"
                        title="Edit"
                        onClick={() => setIsEditMode(true)}
                      />
                    </div>
                  )}
                </div>
                <div
                  className="kendo-rich-text"
                  dangerouslySetInnerHTML={{
                    __html: procedure.description
                  }}
                ></div>
              </div>
            )}
            {props.editorMode === ProcedureViewMode.Edit &&
              procedure.actions.length === 0 && (
                <div className="add-first-action-button-container">
                  <TextButton
                    text="Add Action"
                    type="primary"
                    onClick={() => addAction(-1)}
                    disabled={isActionRequestInProgress}
                  />
                </div>
              )}
            {procedure.actions.map((action, index) => (
              <React.Fragment key={`${action.id}_${action.order}_${index}`}>
                <ProcedureActionEditor
                  headerNumber={index + 1}
                  action={action}
                  readOnly={isReadOnlyView || isPublishRequest || isEnhancing}
                  isEditing={!action.title}
                  editorTools={editorTools}
                  persistChanges={actionUpdated}
                  onImageClick={() => setImageViewIndex(index)}
                  addActionAfterIndex={addAction}
                ></ProcedureActionEditor>
              </React.Fragment>
            ))}
          </div>
          {!isMobile && isVersionHistoryOpen && (
            <ResizablePanel>
              <div className="version-history-container">
                <div className="version-history-heading">
                  <span>Version History</span>
                  <IconButton
                    iconClass="fa-x"
                    title="Close"
                    testId="procedure-version-history-close-button"
                    onClick={() => setIsVersionHistoryOpen(false)}
                  />
                </div>
                <ProcedureVersionHistoryPanel
                  key={versionHistoryRefreshKey}
                  procedureId={procedure.id}
                  onRestoreComplete={() => {
                    $state.reload();
                  }}
                />
              </div>
            </ResizablePanel>
          )}
        </div>
        {imageViewIndex !== null && (
          <ProcedureActionImageView
            action={procedure.actions[imageViewIndex]}
            headerIndex={imageViewIndex + 1}
            onClose={() => setImageViewIndex(null)}
            onNavigateBackwards={
              imageViewIndex > 0
                ? () => setImageViewIndex(imageViewIndex - 1)
                : undefined
            }
            onNavigateForwards={
              imageViewIndex < procedure.actions.length - 1
                ? () => setImageViewIndex(imageViewIndex + 1)
                : undefined
            }
          ></ProcedureActionImageView>
        )}
        {isPublishModalOpen && (
          <PublishProcedureModal
            procedure={procedure}
            isPublishRequest={
              props.editorMode === ProcedureViewMode.PublishRequest
            }
            onClose={() => setIsPublishModalOpen(false)}
            onPublishOrRequest={handlePublishedProcedure}
          />
        )}
        {isDetailsModalOpen && (
          <ProcedureDetailsModal
            procedure={procedure}
            onClose={() => setIsDetailsModalOpen(false)}
            onSave={(updatedProcedure) => {
              setProcedure(updatedProcedure);
              setIsDetailsModalOpen(false);
            }}
          />
        )}
      </div>
    )
  );
};

export default ProcedureEditor;
